import { Controller } from "@hotwired/stimulus"
import { observeViewportEntry } from "../lib/viewport_midpoint.js"

const phases = [
  { name: "question", at: 0, text: "" },
  { name: "review", at: 1800, text: "Using context…" },
  { name: "code", at: 3400, text: "Finding moments…" },
  { name: "find", at: 5000, text: "Comparing views…" },
  { name: "apply", at: 6600, text: "Linking to sources…" },
  { name: "complete", at: 7700, text: "All steps complete." }
]
const duration = 8600

// One cancellable clock drives the illustration, never a network request or agent call.
export default class extends Controller {
  static targets = ["controls", "step", "announcement"]

  connect() {
    this.running = false
    this.manuallyPaused = false
    this.visible = false
    this.frame = null
    this.elapsed = 0
    this.lastTime = null
    this.motion = window.matchMedia("(prefers-reduced-motion: reduce)")
    this.motionChanged = () => {
      if (this.motion.matches) this.finish()
      else this.updatePlayback()
    }
    this.motion.addEventListener("change", this.motionChanged)
    const preview = document.documentElement.hasAttribute("data-turbo-preview")
    this.element.dataset.phase = preview || this.motion.matches || this.element.hasAttribute("data-played") ? "done" : "ready"
    this.updatePlayback()
    if (preview) return
    this.observer = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting && entry.intersectionRatio > 0
      this.updatePlayback()
    }, { threshold: [0, 0.1] })
    this.observer.observe(this.element)
    this.stopWatchingStart = observeViewportEntry(this.element, () => {
      if (!this.element.hasAttribute("data-played")) this.play()
    })
  }

  disconnect() {
    this.finish()
    this.observer?.disconnect()
    this.stopWatchingStart?.()
    this.motion.removeEventListener("change", this.motionChanged)
  }

  toggle() {
    if (!this.running) {
      this.play()
      return
    }
    this.manuallyPaused = !this.manuallyPaused
    this.updatePlayback()
  }

  play() {
    this.stopWatchingStart?.()
    this.cancelFrame()
    this.manuallyPaused = false
    this.elapsed = 0
    this.lastPhase = null
    this.element.setAttribute("data-played", "")
    this.element.removeAttribute("data-animating")
    this.element.removeAttribute("data-response-visible")
    this.element.setAttribute("data-reset", "")
    this.element.dataset.phase = "ready"
    this.element.getBoundingClientRect()
    this.element.removeAttribute("data-reset")
    if (this.motion.matches) {
      this.finish()
      return
    }
    this.running = true
    this.element.setAttribute("data-animating", "")
    this.renderTimeline()
    this.updatePlayback()
  }

  visibilityChanged() {
    this.updatePlayback()
  }

  updatePlayback() {
    this.controlsTarget.hidden = this.motion.matches
    this.controlsTarget.textContent = this.running ? (this.manuallyPaused ? "Play animation" : "Pause animation") : (this.element.dataset.phase === "done" ? "Replay animation" : "Play animation")
    if (!this.running) return
    const paused = this.manuallyPaused || !this.visible || document.hidden
    this.element.toggleAttribute("data-paused", paused)
    if (paused) this.cancelFrame()
    else if (this.frame === null) this.frame = requestAnimationFrame(time => this.tick(time))
  }

  tick(time) {
    this.frame = null
    if (this.lastTime !== null) this.elapsed += time - this.lastTime
    this.lastTime = time
    if (this.elapsed >= duration) {
      this.finish()
      return
    }
    this.renderTimeline()
    this.frame = requestAnimationFrame(next => this.tick(next))
  }

  renderTimeline() {
    const index = phases.findLastIndex(phase => this.elapsed >= phase.at)
    const phase = phases[index]
    this.element.dataset.phase = phase.name
    this.element.toggleAttribute("data-response-visible", this.elapsed >= 8100)
    if (this.lastPhase !== index) {
      this.updateSteps(index - 1)
      this.announcementTarget.textContent = phase.name === "question" ? "Your question." : phase.text
      this.lastPhase = index
    }
  }

  updateSteps(current) {
    this.stepTargets.forEach((step, index) => {
      const state = index < current ? "complete" : index === current ? "active" : "pending"
      step.dataset.state = state
      step.querySelector("[data-step-state]").textContent = state === "active" ? ", in progress" : `, ${state}`
      if (state === "active") step.setAttribute("aria-current", "step")
      else step.removeAttribute("aria-current")
    })
  }

  cancelFrame() {
    cancelAnimationFrame(this.frame)
    this.frame = null
    this.lastTime = null
  }

  finish() {
    this.running = false
    this.manuallyPaused = false
    this.cancelFrame()
    this.element.removeAttribute("data-animating")
    this.element.removeAttribute("data-paused")
    this.element.setAttribute("data-response-visible", "")
    this.element.dataset.phase = "done"
    this.updateSteps(this.stepTargets.length)
    this.announcementTarget.textContent = "Illustration complete. Your response is shown below your question."
    this.updatePlayback()
  }
}
